// @flow
import * as React from 'react';
import {
    Box, Button, Text,
} from 'grommet';
import { Close } from 'grommet-icons';
import { isEmpty } from 'lodash';
import {
    defineMessages, injectIntl, IntlShape,
} from 'react-intl';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import DisconnectIconNoBorder from 'esm-web-pro/components/layout/sidePanel/icons/DisconnectActionNoBorder';
import ConnectIconNoBorder from 'esm-web-pro/components/layout/sidePanel/icons/ConnectIconNoBorder';
import usePrevious from 'esm-web-pro/hooks/usePrevious';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import PipeEITExport from 'esm-web-pro/containers/pages/network/pipe/PipeEITExport';
import PipeAttributes from './PipeAttributes';

type Props = {
    drawingObject: {
        provider: { entity: { name: string } },
        object: Object,
        resource: Object,
        gisResource: Object,
        data: { points: Array<Object>, lines: Array<Object> },
        formData: Object,
    },
    onLastRemove: (layer: Object) => void,
    onSubmit: Function,
    onClose: Function,
    intl: IntlShape,
}

const msg = defineMessages({
    pipes: {
        id: 'pipes-editor-panel-info',
        defaultMessage: 'Pipes',
    },
});

// Function that returns two digit representation of a number
const twoD = (number) => String(number).padStart(2, '0');

const DEFAULT_PROVIDER_COLOR = '#bfce31';

const PipeForm = (props: Props) => {
    const {
        drawingObject: {
            provider, object, data: { points, lines, lineColors }, formData, formDisplayValues,
        }, onLastRemove, onSubmit, onClose, intl: { formatMessage },
    } = props;

    const pointsEndRef = React.useRef(null);
    const [connectedPipes, setConnectedPipes] = React.useState([]);
    const [exportEit, setExportEit] = React.useState(false);
    const [disableExportEit, setDisableExportEit] = React.useState(false);

    const scrollToBottom = () => {
        if (pointsEndRef.current) {
            pointsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const connectPipe = (index) => {
        setConnectedPipes((prevState) => [...prevState, index]);
    };

    const disconnectPipe = (index) => {
        setConnectedPipes((prevState) => prevState.filter((idx) => idx !== index));
    };

    React.useEffect(() => {
        if (isEmpty(lineColors)) {
            if (disableExportEit) {
                setDisableExportEit(false);
            }
            return;
        }

        if (!Object.values(lineColors).every((color) => color === DEFAULT_PROVIDER_COLOR)) {
            setExportEit(true);
        }

        if (Object.values(lineColors).every((color) => color !== DEFAULT_PROVIDER_COLOR)
            && provider.entity.name === process.env.REACT_APP_DEFAULT_PROVIDER) {
            setDisableExportEit(true);
        } else {
            setDisableExportEit(false);
        }
    }, [lineColors]);

    const prevLines = usePrevious(lines);

    React.useEffect(() => {
        if (lines && prevLines && lines.length - prevLines.length === 1) {
            if (points[points.length - 1].feature.properties?.point?.child.type === 'fork') {
                connectPipe(lines.length - 1);
            } else if (connectedPipes.includes(lines.length - 3)) {
                connectPipe(lines.length - 2);
            }
        }
    }, [lines, prevLines, connectedPipes, points]);

    const handleLastRemove = (index) => {
        disconnectPipe(index);
        onLastRemove(points[index + 1]);
    };

    const renderPipes = () => (
        <Box flex={{ shrink: 0 }}>
            {points.length === 1 ? (
                <Box align="center" direction="row" key={points[0]._leaflet_id}>
                    <Box margin={{ vertical: 'small' }} width="100%">
                        <Text>{twoD(1)}</Text>
                    </Box>
                    <Button plain alignSelf="center" icon={<Close />} onClick={() => onLastRemove(points[0])} />
                </Box>
            ) : points.map((point, index) => {
                const startNumber = twoD(index + 1);
                const endNumber = twoD(index + 2);

                return (lines.length > index && (
                    <Box margin={{ top: 'small' }}>
                        <Box align="center" direction="row" key={point._leaflet_id}>
                            <Box width="100%">
                                <Text>
                                    {`${startNumber} - ${formData.count ? formData.count : 0
                                    } ${formatMessage(msg.pipes)} - ${endNumber}`}
                                </Text>
                            </Box>
                            {points.length > 2 && index < points.length - 2 && (
                                <Button
                                    plain
                                    alignSelf="end"
                                    icon={connectedPipes.includes(index)
                                        ? <ConnectIconNoBorder width="40px" height="40px" />
                                        : <DisconnectIconNoBorder width="40px" height="40px" />}
                                    onClick={connectedPipes.includes(index) ? () => disconnectPipe(index)
                                        : () => connectPipe(index)}
                                    pad={{ vertical: 'xsmall' }}
                                    focusIndicator={false}
                                    disabled={points[index + 1].feature.properties?.point?.child.type === 'fork'}
                                />
                            )}

                            {index === points.length - 2 && (
                                <Button
                                    plain
                                    alignSelf="end"
                                    icon={<Close />}
                                    onClick={() => handleLastRemove(index)}
                                />
                            )}
                        </Box>
                    </Box>
                ));
            })}
        </Box>
    );

    React.useEffect(scrollToBottom);
    return (
        <Box height="100%" pad={{ horizontal: 'small', bottom: '60px' }}>
            <Box flex={{ shrink: 0 }} width="100%">
                <HorizontalLine color="grey" />
                <PipeAttributes
                    objectName={object.name}
                    pipeData={formData}
                    pipeModel={formDisplayValues.type}
                    providerName={provider.entity.name}
                />
                <HorizontalLine color="grey" />
            </Box>
            <Box
                direction="row"
                gap="medium"
                pad={{ bottom: 'small', horizontal: 'small' }}
                align="center"
                height="100%"
                width="100%"
                overflow="auto"
                style={{ position: 'unset' }}
            >
                <Box alignSelf="stretch" flex>
                    <PipeEITExport
                        disable={disableExportEit}
                        value={exportEit}
                        onChange={(() => setExportEit((prevValue) => !prevValue))}
                    />
                    {points && renderPipes()}
                    <Box ref={pointsEndRef} />
                </Box>
            </Box>
            <ActionButtons
                onSave={() => onSubmit({ connectedPipes, exportEit })}
                disabled={(points && points[points.length - 1]?.feature.properties?.point?.child.type === 'fork')
                || isEmpty(points) || isEmpty(lines)}
                onClose={onClose}
            />
        </Box>
    );
};

export default injectIntl(PipeForm);
